/** /academy/oral — the mock oral with a trainer, bought per session (D-142): choose the exam (and
 * the Goethe level), read the official outline, pick a free slot (the learner's time zone), read
 * and accept the rules, then pay with the common engine (or a credited session). The learner's
 * sessions: join, calendar, move, cancel, report an absent trainer, read the report and the mark
 * (an estimate), leave a review. Every value comes from the API (data). */

import {
  ApiError,
  type OralBooking,
  type OralSlot,
  academyOralBookingsIcsRetrieve,
  getAcademyOralBookingsListQueryKey,
  getAcademyOralOfferRetrieveQueryKey,
  useAcademyOralBookingsCancelCreate,
  useAcademyOralBookingsCreate,
  useAcademyOralBookingsList,
  useAcademyOralBookingsPayCreate,
  useAcademyOralBookingsRescheduleCreate,
  useAcademyOralBookingsSettleCreate,
  useAcademyOralBookingsTrainerAbsentCreate,
  useAcademyOralOfferRetrieve,
  useAcademyOralOutlineRetrieve,
  useAcademyOralSlotsRetrieve,
} from "@agencyos/api-client";
import { dayLabel, taskDuration, formatXaf, isEuConsumer, localText, oralMark, slotsByDay, zonedWhen } from "@agencyos/core";
import { Badge, type BadgeTone, Button, Checkbox, GlassCard, SelectField, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, Lock, Video } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { goTo } from "../sales/ChoosePlanPage";
import { PageHead } from "../ui";
import { ReviewPrompt } from "./ReviewPrompt";
import "./coaching.css";

const EXAM_NAMES: Record<string, string> = {
  tcf_canada: "TCF Canada",
  tef_canada: "TEF Canada",
  ielts_general: "IELTS General Training",
  goethe: "Goethe-Zertifikat",
};
const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;
const TONES: Record<string, BadgeTone> = {
  pending: "warning",
  confirmed: "offer",
  done: "success",
  cancelled: "default",
  abandoned: "default",
  learner_absent: "danger",
  trainer_absent: "warning",
};

type Texts = Record<string, string>;

function Outline({ exam, level }: { exam: string; level: string }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const outline = useAcademyOralOutlineRetrieve({ exam: exam as never, level: (level || undefined) as never }, { query: { enabled: exam !== "goethe" || Boolean(level) } });
  const data = outline.data?.status === 200 ? outline.data.data : undefined;
  if (outline.isLoading) return <Skeleton style={{ height: 120 }} />;
  if (!data) return null;
  const tasks = (Array.isArray(data.tasks) ? data.tasks : []) as { number: number; label: Texts; seconds: number; prep_seconds: number; goal: Texts }[];
  return (
    <section className="co-outline" aria-labelledby="co-outline-title">
      <h2 id="co-outline-title" className="ac-title-3">
        {t("coaching.outline.title")}
      </h2>
      <ol className="co-outline__tasks">
        {tasks.map((task) => (
          <li key={task.number}>
            <strong>{t("coaching.outline.task", { n: task.number })}</strong> · {localText(task.label, language)}
            <span className="ac-footnote">
              {" "}
              — {t("coaching.outline.speak", { d: taskDuration(task.seconds, language) })},{" "}
              {task.prep_seconds ? t("coaching.outline.prep", { d: taskDuration(task.prep_seconds, language) }) : t("coaching.outline.noPrep")}
            </span>
          </li>
        ))}
      </ol>
      <p className="ac-footnote">
        {data.official_seconds ? t("coaching.outline.total", { d: taskDuration(data.official_seconds, language) }) : null}{" "}
        {exam === "goethe" && data.prep_seconds ? t("coaching.outline.prepTotal", { d: taskDuration(data.prep_seconds, language) }) : null}{" "}
        {t("coaching.outline.session", { minutes: data.session_minutes })}
      </p>
    </section>
  );
}

function Slots({ exam, level, zone, selected, onPick }: { exam: string; level: string; zone: string; selected: OralSlot | null; onPick: (slot: OralSlot) => void }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const slots = useAcademyOralSlotsRetrieve({ exam: exam as never, level: (level || undefined) as never }, { query: { enabled: exam !== "goethe" || Boolean(level) } });
  const data = slots.data?.status === 200 ? slots.data.data : undefined;
  const days = useMemo(() => slotsByDay(data?.slots ?? []), [data]);
  if (slots.isLoading) return <Skeleton style={{ height: 160 }} />;
  return (
    <section className="co-slots" aria-labelledby="co-slots-title">
      <h2 id="co-slots-title" className="ac-title-3">
        {t("coaching.slots.title")}
      </h2>
      <p className="ac-footnote">{t("coaching.slots.zone", { zone: data?.timezone || zone })}</p>
      {days.length === 0 ? <p className="ac-secondary">{t("coaching.slots.none")}</p> : null}
      {days.map((day) => (
        <fieldset key={day.date} className="co-day">
          <legend className="co-day__label">{dayLabel(day.date, language)}</legend>
          <div className="co-day__slots">
            {day.slots.map((slot) => {
              const on = selected?.starts_at === slot.starts_at && selected.trainer === slot.trainer;
              return (
                <button
                  key={`${slot.starts_at}-${slot.trainer}`}
                  type="button"
                  className={`co-slot${on ? " co-slot--on" : ""}`}
                  aria-pressed={on}
                  aria-label={t("coaching.slots.pick", { time: slot.local_time, name: slot.trainer_name })}
                  onClick={() => onPick(slot)}
                >
                  <span className="ao-num">{slot.local_time}</span>
                  <span className="ac-footnote">{t("coaching.slots.with", { name: slot.trainer_name })}</span>
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}
    </section>
  );
}

async function downloadIcs(id: string): Promise<void> {
  const response = await academyOralBookingsIcsRetrieve(id);
  const text = typeof response.data === "string" ? response.data : String(response.data ?? "");
  const url = URL.createObjectURL(new Blob([text], { type: "text/calendar" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "oral-blanc.ics";
  link.click();
  URL.revokeObjectURL(url);
}

function BookingCard({ booking, zone, onChanged }: { booking: OralBooking; zone: string; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const when = zonedWhen(booking.starts_at, booking.timezone || zone, language);
  const cancel = useAcademyOralBookingsCancelCreate({ mutation: { onSuccess: onChanged } });
  const absent = useAcademyOralBookingsTrainerAbsentCreate({ mutation: { onSuccess: onChanged } });
  const settle = useAcademyOralBookingsSettleCreate({ mutation: { onSuccess: onChanged } });
  const reschedule = useAcademyOralBookingsRescheduleCreate({ mutation: { onSuccess: onChanged } });
  const [moving, setMoving] = useState(false);
  const [slot, setSlot] = useState<OralSlot | null>(null);
  const policy = (booking.policy ?? {}) as { change_hours_before?: number; cancel_in_time?: string[]; trainer_no_show?: string[] };
  const result = (booking.result ?? {}) as Record<string, unknown>;
  const mark = oralMark(result);
  const error = [cancel.error, absent.error, settle.error, reschedule.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  return (
    <GlassCard as="article" className="co-booking" aria-label={t("coaching.when", when)}>
      <div className="co-booking__head">
        <strong>{EXAM_NAMES[booking.exam] ?? booking.exam}{booking.exam_level ? ` ${booking.exam_level}` : ""}</strong>
        <Badge tone={TONES[booking.status] ?? "default"}>{t(`coaching.status.${booking.status}`)}</Badge>
        {booking.settlement ? <Badge>{t(`coaching.settlement.${booking.settlement}`)}</Badge> : null}
        {booking.is_test ? <Badge tone="warning">TEST</Badge> : null}
      </div>
      <p className="ac-secondary">
        {t("coaching.when", when)} · {t("coaching.trainer", { name: booking.trainer_name })}
      </p>
      {booking.status === "confirmed" ? (
        <div className="co-booking__actions">
          {booking.join_url ? (
            <a className="ao-button ao-button--primary" href={booking.join_url} target="_blank" rel="noreferrer">
              <Video aria-hidden size={16} /> {t("coaching.join")}
            </a>
          ) : (
            <p className="ac-footnote">{t("coaching.joinSoon")}</p>
          )}
          <Button variant="secondary" onClick={() => void downloadIcs(booking.id)}>
            <CalendarPlus aria-hidden size={16} /> {t("coaching.ics")}
          </Button>
          {booking.can_change ? (
            <>
              <Button variant="secondary" onClick={() => setMoving((v) => !v)}>
                {t("coaching.reschedule")}
              </Button>
              {(policy.cancel_in_time ?? ["credit"]).includes("credit") ? (
                <Button variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate({ id: booking.id, data: { choice: "credit" } })}>
                  {t("coaching.cancelCredit")}
                </Button>
              ) : null}
              {booking.refundable && (policy.cancel_in_time ?? []).includes("refund") ? (
                <Button variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate({ id: booking.id, data: { choice: "refund" } })}>
                  {t("coaching.cancelRefund")}
                </Button>
              ) : null}
            </>
          ) : (
            <p className="ac-footnote">{t("coaching.tooLate", { hours: policy.change_hours_before ?? 24 })}</p>
          )}
          {booking.can_report_absence ? (
            <div className="co-booking__absent" role="group" aria-label={t("coaching.trainerAbsent")}>
              <span className="ac-footnote">{t("coaching.trainerAbsent")}</span>
              <Button variant="secondary" onClick={() => absent.mutate({ id: booking.id, data: { choice: "credit" } })}>
                {t("coaching.absentCredit")}
              </Button>
              {booking.refundable ? (
                <Button variant="ghost" onClick={() => absent.mutate({ id: booking.id, data: { choice: "refund" } })}>
                  {t("coaching.absentRefund")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {booking.settlement === "credit" && booking.refundable ? (
        <Button variant="ghost" onClick={() => settle.mutate({ id: booking.id, data: { choice: "refund" } })}>
          {t("coaching.askRefund")}
        </Button>
      ) : null}
      {moving ? (
        <div className="co-booking__move">
          <h3 className="ac-title-3">{t("coaching.rescheduleTitle")}</h3>
          <Slots exam={booking.exam} level={booking.exam_level ?? ""} zone={zone} selected={slot} onPick={setSlot} />
          <Button
            disabled={!slot || reschedule.isPending}
            onClick={() =>
              slot &&
              reschedule.mutate(
                { id: booking.id, data: { starts_at: slot.starts_at, trainer: slot.trainer } },
                { onSuccess: () => setMoving(false) },
              )
            }
          >
            {t("coaching.reschedule")}
          </Button>
        </div>
      ) : null}
      {booking.status === "done" ? (
        <section className="co-report" aria-labelledby={`co-report-${booking.id}`}>
          <h3 id={`co-report-${booking.id}`} className="ac-title-3">
            {t("coaching.report.title")}
          </h3>
          {mark ? <p className="co-report__mark ao-num">{t("coaching.report.mark", { mark })}</p> : null}
          <p className="ac-footnote">{t("coaching.report.estimate")}</p>
          <p className="co-report__text">{booking.report}</p>
        </section>
      ) : null}
      {error?.detail ? (
        <p className="ko" role="alert">
          {error.detail}
        </p>
      ) : null}
    </GlassCard>
  );
}

export function OralPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "fr";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const offer = useAcademyOralOfferRetrieve({ query: { retry: false } });
  const data = offer.data?.status === 200 ? offer.data.data : undefined;
  const bookings = useAcademyOralBookingsList({ query: { retry: false } });
  const mine: OralBooking[] = bookings.data?.status === 200 ? bookings.data.data : [];
  const exams = data?.exams ?? [];
  const [exam, setExam] = useState(params.get("exam") ?? "");
  const [level, setLevel] = useState("B1");
  const chosenExam = exam && exams.includes(exam) ? exam : (exams[0] ?? "");
  const [slot, setSlot] = useState<OralSlot | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [waiver, setWaiver] = useState(false);
  const [method, setMethod] = useState("");
  const [error, setError] = useState<string | null>(null);
  const book = useAcademyOralBookingsCreate();
  const pay = useAcademyOralBookingsPayCreate();
  const rulesId = useId();
  const zone = data?.timezone || "Africa/Douala";
  const methods = (Array.isArray(data?.methods) ? data?.methods : []) as { key: string; label: Texts; provider_name: string; simulated: boolean }[];
  const chosenMethod = methods.find((m) => m.key === method) ?? methods[0];
  const eu = isEuConsumer(data?.country ?? "");

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getAcademyOralBookingsListQueryKey() });
    void queryClient.invalidateQueries({ queryKey: getAcademyOralOfferRetrieveQueryKey() });
  };

  const onBook = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!slot) return;
    if (!accepted) return setError(t("coaching.rules.required"));
    if (!data?.credit && eu && !waiver) return setError(t("academySales.plans.withdrawalRequired"));
    book.mutate(
      { data: { exam: chosenExam as never, level: chosenExam === "goethe" ? level : "", starts_at: slot.starts_at, trainer: slot.trainer, accept_rules: true } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          const booking = response.data;
          if (booking.status === "confirmed") {
            refresh();
            setSlot(null);
            return;
          }
          if (!chosenMethod) return setError(t("academySales.plans.noMethod"));
          pay.mutate(
            { id: booking.id, data: { method: chosenMethod.key, withdrawal_waiver: waiver } },
            {
              onSuccess: (paid) => {
                if (paid.status === 201) goTo(paid.data.redirect_url, navigate);
              },
              onError: (failure) => setError(failure instanceof ApiError && failure.detail ? failure.detail : t("common.error")),
            },
          );
        },
        onError: (failure) => {
          const body = failure instanceof ApiError ? (failure.body as Record<string, unknown> | undefined) : undefined;
          const first = body ? Object.values(body).flat()[0] : undefined;
          setError(typeof first === "string" ? first : t("common.error"));
        },
      },
    );
  };

  return (
    <div className="ac-page co-page">
      <PageHead eyebrow={t("coaching.navShort")} title={t("coaching.title")} subtitle={<p>{t("coaching.intro")}</p>} />
      {offer.isLoading ? <Skeleton style={{ height: 240 }} /> : null}
      {data && !data.available ? <p className="ac-secondary">{t("coaching.unavailable")}</p> : null}
      {mine.length ? (
        <section className="ac-section" aria-labelledby="co-mine">
          <h2 id="co-mine" className="ac-title-2">
            {t("coaching.mine.title")}
          </h2>
          <div className="co-bookings">
            {mine.map((b) => (
              <BookingCard key={b.id} booking={b} zone={zone} onChanged={refresh} />
            ))}
          </div>
        </section>
      ) : null}
      <ReviewPrompt source="coaching" />
      {data?.available ? (
        <GlassCard as="section" level="thick" className="co-book" aria-labelledby="co-book-title">
          <form className="ac-form" onSubmit={onBook} noValidate>
            <h2 id="co-book-title" className="ac-title-2">
              {t("coaching.title")}
            </h2>
            <p className="co-price ao-num">{t("coaching.price", { price: formatXaf(data.price_xaf), minutes: data.session_minutes })}</p>
            {data.credit ? <p role="status" className="notice-success">{t("coaching.credit")}</p> : null}
            <div className="form-grid">
              <SelectField
                label={t("coaching.exam")}
                value={chosenExam}
                options={exams.map((e) => ({ value: e, label: EXAM_NAMES[e] ?? e }))}
                onChange={(event) => {
                  setExam(event.target.value);
                  setSlot(null);
                }}
              />
              {chosenExam === "goethe" ? (
                <SelectField
                  label={t("coaching.level")}
                  value={level}
                  options={LEVELS.map((l) => ({ value: l, label: l }))}
                  onChange={(event) => {
                    setLevel(event.target.value);
                    setSlot(null);
                  }}
                />
              ) : null}
            </div>
            {chosenExam ? <Outline exam={chosenExam} level={chosenExam === "goethe" ? level : ""} /> : null}
            {chosenExam ? <Slots exam={chosenExam} level={chosenExam === "goethe" ? level : ""} zone={zone} selected={slot} onPick={setSlot} /> : null}
            <section className="co-rules notice-warning" aria-labelledby={rulesId}>
              <h3 id={rulesId} className="ac-title-3">
                {t("coaching.rules.title")}
              </h3>
              <ul>
                <li>{t("coaching.rules.change", { hours: data.change_hours_before })}</li>
                <li>{t("coaching.rules.cancel", { days: data.refund_days })}</li>
                <li>{t("coaching.rules.learnerAbsent", { minutes: data.late_minutes, hours: data.change_hours_before })}</li>
                <li>{t("coaching.rules.trainerAbsent")}</li>
                <li>{t("coaching.rules.report", { hours: data.report_hours })}</li>
              </ul>
              <Checkbox label={t("coaching.rules.accept")} checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
            </section>
            {!data.credit && methods.length > 1 ? (
              <SelectField
                label={t("academySales.plans.method")}
                value={chosenMethod?.key ?? ""}
                options={methods.map((m) => ({ value: m.key, label: localText(m.label, language) }))}
                onChange={(event) => setMethod(event.target.value)}
              />
            ) : null}
            {!data.credit && eu ? <Checkbox label={t("academySales.plans.withdrawal")} checked={waiver} onChange={(event) => setWaiver(event.target.checked)} /> : null}
            {error ? (
              <p className="ko" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" block disabled={!slot || book.isPending || pay.isPending}>
              <Lock aria-hidden size={16} />{" "}
              {pay.isPending
                ? t("coaching.paying")
                : data.credit
                  ? t("coaching.bookWithCredit")
                  : `${t("coaching.book")} · ${formatXaf(data.price_xaf)}`}
            </Button>
            {chosenMethod?.provider_name && !data.credit ? (
              <p className="ac-footnote">{t("academySales.plans.secureWith", { provider: chosenMethod.provider_name })}</p>
            ) : null}
            {methods.some((m) => m.simulated) ? <p className="ac-footnote as-test">{t("academySales.plans.test")}</p> : null}
          </form>
        </GlassCard>
      ) : null}
    </div>
  );
}
